// src/lib/svg/parseSvg.js Parse SVG markup from external apps (Figma, Illustrator, Inkscape) into internal display objects.

import { newObjectId } from '~/lib/objectIds'
import { rawPathBounds } from '~/lib/objectBounds'
import { WIDTH, HEIGHT } from '~/lib/constants'
import { originToPivot, PIVOT_VERSION } from '~/lib/migrations/steps/originToPivot'
import { stringToRawPath, transformRawPath, resetPathOrigin } from './paths'
import { getRectPathData, getEllipsePathData } from './shapes'

const SHAPE_TAGS = new Set(['path', 'rect', 'circle', 'ellipse', 'polygon', 'polyline', 'line'])
const SKIP_TAGS = new Set(['clipPath', 'symbol', 'text', 'image', 'title', 'desc', 'metadata'])

const MAX_OBJECTS = 50
const MAX_SVG_SIZE = 100_000
const MAX_NESTING = 3

// --- Matrix helpers ---

const IDENTITY = [1, 0, 0, 1, 0, 0] // [a, b, c, d, tx, ty]

/** Multiply two 2×3 affine matrices: result = A * B */
function multiplyMatrix(A, B) {
	const [a1, b1, c1, d1, tx1, ty1] = A
	const [a2, b2, c2, d2, tx2, ty2] = B
	return [
		a1 * a2 + c1 * b2,
		b1 * a2 + d1 * b2,
		a1 * c2 + c1 * d2,
		b1 * c2 + d1 * d2,
		a1 * tx2 + c1 * ty2 + tx1,
		b1 * tx2 + d1 * ty2 + ty1,
	]
}

/** Decompose a 2×3 affine matrix into translation, rotation (degrees), and scale */
function decomposeMatrix(m) {
	const [a, b, c, d, tx, ty] = m
	const result = { x: 0, y: 0 }
	if (Math.abs(tx) > 0.05) result.x = Math.round(tx * 10) / 10
	if (Math.abs(ty) > 0.05) result.y = Math.round(ty * 10) / 10
	const scaleX = Math.sqrt(a * a + b * b)
	const det = a * d - b * c
	const scaleY = det >= 0 ? Math.sqrt(c * c + d * d) : -Math.sqrt(c * c + d * d)
	const rotation = Math.round(Math.atan2(b, a) * (180 / Math.PI) * 10) / 10
	if (rotation !== 0) result.rotation = rotation
	const sx = Math.round(scaleX * 10000) / 10000
	const sy = Math.round(scaleY * 10000) / 10000
	if (sx !== 1) result.scaleX = sx
	if (sy !== 1) result.scaleY = sy
	return result
}

/** Parse an SVG transform attribute string into a single 2×3 matrix */
function parseSvgTransform(str) {
	if (!str) return IDENTITY
	let result = IDENTITY
	const re = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/gi
	let m
	while ((m = re.exec(str)) !== null) {
		const type = m[1].toLowerCase()
		const args = m[2].split(/[\s,]+/).map(s => parseFloat(s))
		let mat
		switch (type) {
			case 'matrix':
				// SVG matrix(a,b,c,d,e,f) maps to our [a, b, c, d, tx, ty]
				mat = [args[0], args[1], args[2], args[3], args[4], args[5]]
				break
			case 'translate':
				mat = [1, 0, 0, 1, args[0] || 0, args[1] || 0]
				break
			case 'scale': {
				const sx = args[0] || 1
				const sy = args.length > 1 ? args[1] : sx
				mat = [sx, 0, 0, sy, 0, 0]
				break
			}
			case 'rotate': {
				const a = (args[0] * Math.PI) / 180
				const cos = Math.cos(a)
				const sin = Math.sin(a)
				if (args.length === 3) {
					// rotate(angle, cx, cy) = translate(cx,cy) rotate(angle) translate(-cx,-cy)
					const cx = args[1],
						cy = args[2]
					mat = [cos, sin, -sin, cos, cx - cos * cx + sin * cy, cy - sin * cx - cos * cy]
				} else {
					mat = [cos, sin, -sin, cos, 0, 0]
				}
				break
			}
			case 'skewx': {
				const t = Math.tan((args[0] * Math.PI) / 180)
				mat = [1, 0, t, 1, 0, 0]
				break
			}
			case 'skewy': {
				const t = Math.tan((args[0] * Math.PI) / 180)
				mat = [1, t, 0, 1, 0, 0]
				break
			}
			default:
				continue
		}
		result = multiplyMatrix(result, mat)
	}
	return result
}

/** Walk from element up to svgRoot, accumulating transforms (parent-first order) */
function getAccumulatedTransform(element, svgRoot) {
	const chain = []
	let el = element
	while (el && el !== svgRoot) {
		const t = el.getAttribute('transform')
		if (t) chain.unshift(parseSvgTransform(t))
		el = el.parentElement
	}
	// Apply viewBox offset from SVG root
	const viewBox = svgRoot.getAttribute('viewBox')
	if (viewBox) {
		const parts = viewBox.split(/[\s,]+/).map(Number)
		if (parts.length >= 2 && (parts[0] !== 0 || parts[1] !== 0)) {
			chain.unshift([1, 0, 0, 1, -parts[0], -parts[1]])
		}
	}
	let result = IDENTITY
	for (const mat of chain) {
		result = multiplyMatrix(result, mat)
	}
	return result
}

// --- CSS var/calc resolution ---

/** Evaluate a calc() expression with basic arithmetic. Supports deg, px, %, unitless. */
function evalCalc(expr) {
	// Remove nested calc() wrappers
	const s = expr.replace(/calc\(/g, '(')

	// Tokenize: numbers with units, operators, parens
	const tokens = []
	const re = /([+-]?[\d.]+(?:e[+-]?\d+)?)\s*(deg|px|%)?|([+\-*/])|([()])/gi
	let m
	while ((m = re.exec(s)) !== null) {
		if (m[1] !== undefined) tokens.push({ type: 'num', value: parseFloat(m[1]), unit: (m[2] || '').toLowerCase() })
		else if (m[3]) tokens.push({ type: 'op', value: m[3] })
		else if (m[4]) tokens.push({ type: m[4] === '(' ? 'lparen' : 'rparen' })
	}

	// Shunting-yard → RPN
	const output = []
	const ops = []
	const prec = { '+': 1, '-': 1, '*': 2, '/': 2 }
	for (const tok of tokens) {
		if (tok.type === 'num') output.push(tok)
		else if (tok.type === 'op') {
			while (
				ops.length &&
				ops[ops.length - 1].type === 'op' &&
				prec[ops[ops.length - 1].value] >= prec[tok.value]
			) {
				output.push(ops.pop())
			}
			ops.push(tok)
		} else if (tok.type === 'lparen') ops.push(tok)
		else if (tok.type === 'rparen') {
			while (ops.length && ops[ops.length - 1].type !== 'lparen') output.push(ops.pop())
			ops.pop()
		}
	}
	while (ops.length) output.push(ops.pop())

	// Evaluate RPN
	const stack = []
	for (const tok of output) {
		if (tok.type === 'num') {
			stack.push(tok)
			continue
		}
		const b = stack.pop(),
			a = stack.pop()
		if (!a || !b) return null
		let val,
			unit = a.unit || b.unit
		switch (tok.value) {
			case '+':
				val = a.value + b.value
				break
			case '-':
				val = a.value - b.value
				break
			case '*':
				val = a.value * b.value
				unit = a.unit || b.unit
				break
			case '/':
				val = b.value !== 0 ? a.value / b.value : 0
				unit = a.unit
				break
		}
		stack.push({ type: 'num', value: val, unit })
	}
	if (!stack.length) return null
	const result = stack[0]
	const rounded = Math.round(result.value * 1000) / 1000
	return rounded + (result.unit || '')
}

/** Resolve var() and calc() in a CSS value string given a vars map */
function resolveValue(val, vars) {
	// Substitute var() references (iterate for nested vars)
	let resolved = val
	for (let i = 0; i < 10; i++) {
		const before = resolved
		resolved = resolved.replace(/var\(\s*(--[\w-]+)\s*(?:,\s*([^)]*))?\)/g, (_, name, fallback) => {
			return vars[name] ?? fallback?.trim() ?? ''
		})
		if (resolved === before) break
	}

	// Evaluate calc() expressions (innermost first)
	for (let i = 0; i < 10; i++) {
		const before = resolved
		resolved = resolved.replace(/calc\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g, (_, expr) => {
			return evalCalc(expr) ?? _
		})
		if (resolved === before) break
	}

	return resolved
}

// --- Element → path data conversion ---

function num(el, attr, fallback = 0) {
	const v = el.getAttribute(attr)
	return v != null ? parseFloat(v) : fallback
}

function elementToPathData(el) {
	const tag = el.tagName.toLowerCase()
	switch (tag) {
		case 'path':
			return el.getAttribute('d')
		case 'rect': {
			const x = num(el, 'x'),
				y = num(el, 'y')
			const w = num(el, 'width'),
				h = num(el, 'height')
			if (!w || !h) return null
			return getRectPathData(x, y, w, h)
		}
		case 'circle': {
			const cx = num(el, 'cx'),
				cy = num(el, 'cy'),
				r = num(el, 'r')
			if (!r) return null
			return getEllipsePathData(cx - r, cy - r, r * 2, r * 2)
		}
		case 'ellipse': {
			const cx = num(el, 'cx'),
				cy = num(el, 'cy')
			const rx = num(el, 'rx'),
				ry = num(el, 'ry')
			if (!rx || !ry) return null
			return getEllipsePathData(cx - rx, cy - ry, rx * 2, ry * 2)
		}
		case 'polygon':
		case 'polyline': {
			const pts = el.getAttribute('points')
			if (!pts) return null
			const coords = pts
				.trim()
				.split(/[\s,]+/)
				.map(Number)
			if (coords.length < 4) return null
			let d = `M${coords[0]},${coords[1]}`
			for (let i = 2; i < coords.length; i += 2) {
				d += `L${coords[i]},${coords[i + 1]}`
			}
			if (tag === 'polygon') d += 'Z'
			return d
		}
		case 'line':
			return `M${num(el, 'x1')},${num(el, 'y1')}L${num(el, 'x2')},${num(el, 'y2')}`
		default:
			return null
	}
}

// --- Style extraction ---

/** Parse a CSS color to hex string. Returns null for gradients/patterns/url(). */
function parseColor(str, currentColor = '#ffffff') {
	if (!str || str === 'none' || str === 'transparent') return ''
	if (str === 'currentColor') return currentColor
	if (str.startsWith('url(')) return null // gradient/pattern
	// Already hex
	if (/^#[0-9a-fA-F]{3,8}$/.test(str)) {
		if (str.length === 4) {
			return '#' + str[1] + str[1] + str[2] + str[2] + str[3] + str[3]
		}
		return str.slice(0, 7)
	}
	// rgb()/rgba()
	const m = str.match(/rgba?\(\s*([\d.]+)[,%\s]+([\d.]+)[,%\s]+([\d.]+)/)
	if (m) {
		const hex = c => Math.round(parseFloat(c)).toString(16).padStart(2, '0')
		return '#' + hex(m[1]) + hex(m[2]) + hex(m[3])
	}
	// Common named colors
	const named = { white: '#ffffff', black: '#000000', red: '#ff0000', green: '#008000', blue: '#0000ff' }
	if (named[str.toLowerCase()]) return named[str.toLowerCase()]
	return str
}

const INHERITED_SVG_PROPS = new Set(['fill', 'stroke', 'stroke-width', 'opacity', 'fill-opacity'])

function getStyleProp(el, prop, cssProp) {
	const attrName = cssProp || prop
	const s = el.style?.[prop]
	if (s) return s
	const a = el.getAttribute(attrName)
	if (a) return a
	if (INHERITED_SVG_PROPS.has(attrName)) {
		let parent = el.parentElement
		while (parent && parent.tagName !== 'svg') {
			const ps = parent.style?.[prop]
			if (ps) return ps
			const pa = parent.getAttribute(attrName)
			if (pa) return pa
			parent = parent.parentElement
		}
	}
	return null
}

function extractStyle(el, currentColor) {
	const style = {}

	// Fill
	const fillRaw = getStyleProp(el, 'fill')
	if (fillRaw === 'none') {
		style.fill = ''
	} else if (fillRaw) {
		const c = parseColor(fillRaw, currentColor)
		if (c !== null) style.fill = c
	}

	// Stroke
	const strokeRaw = getStyleProp(el, 'stroke')
	if (strokeRaw && strokeRaw !== 'none') {
		const c = parseColor(strokeRaw, currentColor)
		if (c !== null) style.stroke = c
	}

	// Stroke width
	const sw = getStyleProp(el, 'strokeWidth', 'stroke-width')
	if (sw) {
		const v = parseFloat(sw)
		if (v > 0 && v <= 40) style.strokeWidth = v
	}

	// Opacity
	const opStr = getStyleProp(el, 'opacity')
	const fillOpStr = getStyleProp(el, 'fillOpacity', 'fill-opacity')
	let op = opStr ? parseFloat(opStr) : 1
	if (fillOpStr) op *= parseFloat(fillOpStr)
	if (op < 1) style.opacity = Math.round(op * 100)

	return style
}

// --- Preprocessing: <use> resolution, <style> application ---

/** Resolve all <use> elements by cloning their referenced content in-place */
function resolveUseElements(svgRoot) {
	const uses = [...svgRoot.querySelectorAll('use')]
	for (const use of uses) {
		const href = use.getAttribute('href') || use.getAttributeNS('http://www.w3.org/1999/xlink', 'href')
		if (!href || !href.startsWith('#')) continue
		const target = svgRoot.querySelector(href)
		if (!target) continue

		const clone = target.cloneNode(true)
		const refId = clone.getAttribute('id')
		clone.removeAttribute('id')
		if (refId) clone.setAttribute('data-name', refId)

		// Apply <use> x/y as an additional translate
		const ux = parseFloat(use.getAttribute('x')) || 0
		const uy = parseFloat(use.getAttribute('y')) || 0
		const useTransform = use.getAttribute('transform') || ''
		const translate = ux || uy ? `translate(${ux},${uy})` : ''
		const combinedTransform = [useTransform, translate].filter(Boolean).join(' ')
		if (combinedTransform) {
			const existing = clone.getAttribute('transform') || ''
			clone.setAttribute('transform', [combinedTransform, existing].filter(Boolean).join(' '))
		}

		// Copy presentational attributes from <use> that aren't on the clone
		for (const attr of use.attributes) {
			if (['href', 'xlink:href', 'x', 'y', 'transform', 'id'].includes(attr.name)) continue
			if (!clone.hasAttribute(attr.name)) {
				clone.setAttribute(attr.name, attr.value)
			}
		}

		use.replaceWith(clone)
	}
}

/** Parse <style> blocks and apply matched rules as element attributes */
function applyStyleRules(svgRoot) {
	const styleEls = svgRoot.querySelectorAll('style')
	if (!styleEls.length) return

	// First pass: collect all rules and custom property declarations
	const rules = []
	const vars = {}
	for (const styleEl of styleEls) {
		const text = styleEl.textContent
		const re = /([^{}]+)\{([^}]*)\}/g
		let m
		while ((m = re.exec(text)) !== null) {
			const selector = m[1].trim()
			const body = m[2]
			const props = {}
			for (const decl of body.split(';')) {
				const colon = decl.indexOf(':')
				if (colon < 0) continue
				const prop = decl.slice(0, colon).trim()
				const val = decl.slice(colon + 1).trim()
				if (!val) continue
				props[prop] = val
			}
			// Collect custom properties from svg/:root selectors
			if (selector === 'svg' || selector === ':root') {
				for (const [prop, val] of Object.entries(props)) {
					if (prop.startsWith('--')) vars[prop] = val
				}
			}
			if (Object.keys(props).length) {
				rules.push({ selector, props })
			}
		}
	}

	// Resolve var()/calc() in all rule values
	for (const rule of rules) {
		for (const [prop, val] of Object.entries(rule.props)) {
			if (val.includes('var(') || val.includes('calc(')) {
				rule.props[prop] = resolveValue(val, vars)
			}
		}
	}

	// Apply rules to matching elements
	const attrMap = {
		fill: 'fill',
		stroke: 'stroke',
		'stroke-width': 'stroke-width',
		opacity: 'opacity',
		'fill-opacity': 'fill-opacity',
		transform: 'transform',
		'transform-origin': 'transform-origin',
		r: 'r',
		cx: 'cx',
		cy: 'cy',
	}

	for (const { selector, props } of rules) {
		// Skip selectors that target the root svg element itself
		if (selector === 'svg') continue
		let els
		try {
			els = svgRoot.querySelectorAll(selector)
		} catch {
			continue // invalid selector
		}
		for (const el of els) {
			for (const [cssProp, val] of Object.entries(props)) {
				const attr = attrMap[cssProp]
				if (attr && !el.hasAttribute(attr)) {
					el.setAttribute(attr, val)
				}
			}
		}
	}
}

/** Determine currentColor from SVG root's color/style */
function resolveCurrentColor(svgRoot) {
	const color = svgRoot.getAttribute('color') || svgRoot.style?.color
	if (color && color !== 'inherit') {
		const parsed = parseColor(color)
		if (parsed) return parsed
	}
	return '#ffffff'
}

// --- Main parser ---

function formatSvgId(id) {
	if (!id) return undefined
	return id.replace(/[_-]+/g, ' ').replace(/^\s+/, '').trim()
}

function isVisible(el) {
	if (el.getAttribute('display') === 'none') return false
	if (el.getAttribute('visibility') === 'hidden') return false
	if (el.style?.display === 'none') return false
	if (el.style?.visibility === 'hidden') return false
	return true
}

function convertElement(el, stopEl, currentColor) {
	if (!isVisible(el)) return null
	const d = elementToPathData(el)
	if (!d) return null

	const rawPath = stringToRawPath(d)
	if (!rawPath?.length) return null

	const matrix = getAccumulatedTransform(el, stopEl)
	if (
		matrix[0] !== 1 ||
		matrix[1] !== 0 ||
		matrix[2] !== 0 ||
		matrix[3] !== 1 ||
		matrix[4] !== 0 ||
		matrix[5] !== 0
	) {
		transformRawPath(rawPath, matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5])
	}

	const bounds = rawPathBounds(rawPath)
	const width = bounds.width
	const height = bounds.height
	if (width < 0.1 || height < 0.1) return null

	const origin = resetPathOrigin(rawPath)
	const style = extractStyle(el, currentColor)

	// const tag = el.tagName.toLowerCase()
	const type = 'path'
	const name = formatSvgId(el.getAttribute('id') || el.getAttribute('data-name'))

	const obj = {
		type,
		data: origin.data,
		x: origin.x,
		y: origin.y,
		width: Math.round(width * 10) / 10,
		height: Math.round(height * 10) / 10,
		...style,
	}
	if (name) obj.name = name
	return obj
}

/** Determine if a fill color is "dark" (used for SVG mask luminance interpretation) */
function isDarkFill(fillStr) {
	if (!fillStr || fillStr === 'none' || fillStr === 'transparent') return true
	if (fillStr === 'black' || fillStr === '#000' || fillStr === '#000000') return true
	if (/^#[0-9a-f]{6}$/i.test(fillStr)) {
		const r = parseInt(fillStr.slice(1, 3), 16)
		const g = parseInt(fillStr.slice(3, 5), 16)
		const b = parseInt(fillStr.slice(5, 7), 16)
		return (r + g + b) / 3 < 64
	}
	return false
}

/** Parse a <mask> element's children into mask objects.
 *  refMatrix: the accumulated transform of the referencing element (mask content lives in its coordinate space).
 *  Dark fills become destination-out (holes); AlphaFilter on the mask container at runtime makes erase work. */
function parseMaskElement(maskEl, svgRoot, currentColor, refMatrix) {
	const objects = []

	function processShape(shape, parentEl, parentId) {
		const tag = shape.tagName.toLowerCase()
		if (tag === 'rect') {
			const w = parseFloat(shape.getAttribute('width')) || 0
			const h = parseFloat(shape.getAttribute('height')) || 0
			const fill = shape.getAttribute('fill') || ''
			if (w >= 100 && h >= 100 && isDarkFill(fill)) return
		}

		const d = elementToPathData(shape)
		if (!d) return
		const rawPath = stringToRawPath(d)
		if (!rawPath?.length) return

		const localMatrix = getAccumulatedTransform(shape, parentEl)
		const absMatrix = parentId ? localMatrix : multiplyMatrix(refMatrix, localMatrix)
		if (
			absMatrix[0] !== 1 ||
			absMatrix[1] !== 0 ||
			absMatrix[2] !== 0 ||
			absMatrix[3] !== 1 ||
			absMatrix[4] !== 0 ||
			absMatrix[5] !== 0
		) {
			transformRawPath(
				rawPath,
				absMatrix[0],
				absMatrix[1],
				absMatrix[2],
				absMatrix[3],
				absMatrix[4],
				absMatrix[5]
			)
		}

		const bounds = rawPathBounds(rawPath)
		if (bounds.width < 0.1 || bounds.height < 0.1) return

		const origin = resetPathOrigin(rawPath)
		const style = extractStyle(shape, currentColor)

		const obj = {
			type: 'path',
			data: origin.data,
			x: origin.x,
			y: origin.y,
			width: Math.round(bounds.width * 10) / 10,
			height: Math.round(bounds.height * 10) / 10,
			...style,
		}
		if (parentId) obj.parentId = parentId

		const shapeFill = getStyleProp(shape, 'fill') || 'white'
		if (isDarkFill(shapeFill)) {
			obj.blendMode = 'destination-out'
		} else if (!obj.fill) {
			obj.fill = '#ffffff'
		}
		objects.push(obj)
	}

	function processGroup(gEl, parentEl, parentId, depth) {
		if (depth > MAX_NESTING) return
		const groupMatrix = getAccumulatedTransform(gEl, parentEl)
		// Top-level mask groups get refMatrix applied for absolute positioning
		const absMatrix = parentId ? groupMatrix : multiplyMatrix(refMatrix, groupMatrix)
		const groupProps = decomposeMatrix(absMatrix)
		const groupId = newObjectId('group')

		const groupObj = { type: 'group', id: groupId, ...groupProps }
		const gName = formatSvgId(gEl.getAttribute('id') || gEl.getAttribute('data-name'))
		if (gName) groupObj.name = gName
		if (parentId) groupObj.parentId = parentId
		objects.push(groupObj)

		for (const child of gEl.children) {
			const tag = child.tagName.toLowerCase()
			if (SKIP_TAGS.has(tag)) continue
			if (SHAPE_TAGS.has(tag)) {
				processShape(child, gEl, groupId)
			} else if (tag === 'g') {
				processGroup(child, gEl, groupId, depth + 1)
			}
		}
	}

	// Wrap all mask content in a single container group
	const containerId = newObjectId('group')
	const containerObj = { type: 'group', id: containerId, x: 0, y: 0 }
	objects.push(containerObj)

	for (const child of maskEl.children) {
		const tag = child.tagName.toLowerCase()
		if (SKIP_TAGS.has(tag)) continue

		if (SHAPE_TAGS.has(tag)) {
			processShape(child, maskEl, containerId)
		} else if (tag === 'g') {
			processGroup(child, maskEl, containerId, 1)
		}
	}
	return { objects, containerId }
}

/**
 * Parse SVG markup into internal display objects.
 * @param {string} svgText - SVG markup string
 * @returns {{ objects: object[], groups: number[][], masks: { targetIndex: number, maskIndices: number[], inverse: boolean }[] } | null}
 */
export function parseSvgToObjects(svgText) {
	if (!svgText || typeof svgText !== 'string') return null
	if (svgText.length > MAX_SVG_SIZE) return null

	const svgMatch = svgText.match(/<svg[\s\S]*<\/svg>/i)
	if (!svgMatch) return null
	const svgStr = svgMatch[0]

	const doc = new DOMParser().parseFromString(svgStr, 'image/svg+xml')
	if (doc.querySelector('parsererror')) return null

	const svgRoot = doc.documentElement

	applyStyleRules(svgRoot)
	resolveUseElements(svgRoot)
	const currentColor = resolveCurrentColor(svgRoot)

	// Check nesting depth — reject if > MAX_NESTING
	function maxDepth(el, depth) {
		if (depth > MAX_NESTING) return depth
		let max = depth
		for (const child of el.children) {
			if (child.tagName.toLowerCase() === 'g') {
				const d = maxDepth(child, depth + 1)
				if (d > max) max = d
			}
		}
		return max
	}
	if (maxDepth(svgRoot, 0) > MAX_NESTING) return null

	const objects = []
	const masks = []

	const maskDefs = {}
	for (const maskEl of svgRoot.querySelectorAll('mask')) {
		const id = maskEl.getAttribute('id')
		if (id) maskDefs[id] = maskEl
	}

	function walkElement(el, parentEl, parentId, depth) {
		if (objects.length >= MAX_OBJECTS) return
		const tag = el.tagName.toLowerCase()

		if (SKIP_TAGS.has(tag)) return
		if (tag === 'defs' || tag === 'style' || tag === 'mask') return

		if (SHAPE_TAGS.has(tag)) {
			if (!isVisible(el)) return
			const maskAttr = el.getAttribute('mask')
			const maskId = maskAttr?.match(/url\(#([^)]+)\)/)?.[1]

			const obj = convertElement(el, parentEl, currentColor)
			if (!obj) return
			if (parentId) obj.parentId = parentId
			const targetIndex = objects.length
			objects.push(obj)

			if (maskId && maskDefs[maskId]) {
				const refMatrix = getAccumulatedTransform(el, parentEl)
				const maskResult = parseMaskElement(maskDefs[maskId], svgRoot, currentColor, refMatrix)
				if (maskResult.objects.length) {
					const maskIndices = []
					for (const mObj of maskResult.objects) {
						maskIndices.push(objects.length)
						if (!mObj.parentId && parentId) mObj.parentId = parentId
						objects.push(mObj)
					}
					masks.push({ targetIndex, maskIndices, containerId: maskResult.containerId })
				}
			}
		} else if (tag === 'g') {
			if (!isVisible(el)) return
			const groupMatrix = getAccumulatedTransform(el, parentEl)
			const groupProps = decomposeMatrix(groupMatrix)
			const groupId = newObjectId('group')

			const groupObj = { type: 'group', id: groupId, ...groupProps }
			const groupName = formatSvgId(el.getAttribute('id') || el.getAttribute('data-name'))
			if (groupName) groupObj.name = groupName
			if (parentId) groupObj.parentId = parentId
			objects.push(groupObj)

			for (const child of el.children) {
				walkElement(child, el, groupId, depth + 1)
			}
		}
	}

	for (const child of svgRoot.children) {
		walkElement(child, svgRoot, null, 0)
	}

	if (!objects.length) return null

	// Name unnamed mask containers
	let maskNameCounter = 1
	for (const { containerId } of masks) {
		const containerObj = objects.find(o => o.id === containerId)
		if (containerObj && !containerObj.name) {
			containerObj.name = `Mask ${maskNameCounter++}`
		}
	}

	// Centering pass — only adjust top-level objects Build id→object map and compute group bounds from children
	const idMap = {}
	for (const obj of objects) {
		if (obj.id) idMap[obj.id] = obj
	}
	function getObjBounds(obj) {
		if (obj.type === 'group') {
			const children = objects.filter(o => o.parentId === obj.id)
			let cMinX = Infinity,
				cMinY = Infinity,
				cMaxX = -Infinity,
				cMaxY = -Infinity
			for (const child of children) {
				const cb = getObjBounds(child)
				if (cb.x < cMinX) cMinX = cb.x
				if (cb.y < cMinY) cMinY = cb.y
				if (cb.x + cb.w > cMaxX) cMaxX = cb.x + cb.w
				if (cb.y + cb.h > cMaxY) cMaxY = cb.y + cb.h
			}
			if (!isFinite(cMinX)) return { x: obj.x || 0, y: obj.y || 0, w: 0, h: 0 }
			return { x: (obj.x || 0) + cMinX, y: (obj.y || 0) + cMinY, w: cMaxX - cMinX, h: cMaxY - cMinY }
		}
		return { x: obj.x || 0, y: obj.y || 0, w: obj.width || 0, h: obj.height || 0 }
	}

	let minX = Infinity,
		minY = Infinity,
		maxX = -Infinity,
		maxY = -Infinity
	for (const obj of objects) {
		if (obj.parentId) continue
		const b = getObjBounds(obj)
		if (b.x < minX) minX = b.x
		if (b.y < minY) minY = b.y
		if (b.x + b.w > maxX) maxX = b.x + b.w
		if (b.y + b.h > maxY) maxY = b.y + b.h
	}
	if (!isFinite(minX)) return null

	const groupW = maxX - minX
	const groupH = maxY - minY
	const groupCX = (minX + maxX) / 2
	const groupCY = (minY + maxY) / 2

	const scale = Math.min((WIDTH * 0.9) / groupW, (HEIGHT * 0.9) / groupH, 1)
	const dx = -groupCX * scale
	const dy = -groupCY * scale

	const r = v => Math.round(v * 10) / 10
	for (const obj of objects) {
		if (obj.parentId) continue
		if (scale < 1) {
			if (obj.data) {
				const rawPath = stringToRawPath(obj.data)
				transformRawPath(rawPath, scale, 0, 0, scale, 0, 0)
				const origin = resetPathOrigin(rawPath)
				obj.data = origin.data
				obj.width = r(obj.width * scale)
				obj.height = r(obj.height * scale)
			} else if (obj.type === 'group') {
				obj.scaleX = Math.round(scale * 10000) / 10000
				obj.scaleY = Math.round(scale * 10000) / 10000
			}
		}
		obj.x = r((obj.x || 0) * scale + dx)
		obj.y = r((obj.y || 0) * scale + dy)
	}

	toPivotModel(objects)
	return { objects, masks }
}

/**
 * The import builds every object about its data ORIGIN, as SVG geometry is written; the stored model names the pivot. Convert once, through the same transform the settings migration runs (`originToPivot`), so an import and a migrated preset cannot disagree: leaves move to their box centre, a group's children are re-expressed about the group's.
 */
function toPivotModel(objects) {
	const keyOf = new Map(objects.map((o, i) => [o, o.id ?? `__svg_o${i}`]))
	const map = {}
	for (const o of objects) map[keyOf.get(o)] = { ...o, children: o.type === 'group' ? [] : undefined }
	for (const o of objects) if (o.parentId && map[o.parentId]) map[o.parentId].children.push(keyOf.get(o))
	const out = originToPivot({ _v: PIVOT_VERSION - 1, objects: map }).objects
	for (const o of objects) {
		const m = out[keyOf.get(o)]
		o.x = m.x
		o.y = m.y
		if (m.rotation !== undefined) o.rotation = m.rotation
	}
}
